import React from 'react';
import { AlertIcon, AppButton, Box, Inscription, Preloader, WarningIcon } from '@common-fe/common-fe';

import ModalWrapper from '@/components/wrappers/ModalWrapper';

import {
  FraudReviewModalConfirmColor,
  FraudReviewModalKind,
  FraudReviewModalTone,
} from '../fraudReview.constants';
import { useFraudReviewModalConfig } from '../hooks';

interface Props {
  kind: FraudReviewModalKind | null;
  employeeName: string;
  isSubmitting: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const FraudReviewModal: React.FC<Props> = ({
  kind,
  employeeName,
  isSubmitting,
  onConfirm,
  onClose,
}) => {
  const config = useFraudReviewModalConfig({ kind, employeeName });

  if (!config) {
    return null;
  }

  const isDanger = config.tone === FraudReviewModalTone.Danger;

  return (
    <ModalWrapper visible onSetVisible={onClose} testId={config.testId}>
      <Box align="center" pad={{ vertical: 'spacing24' }}>
        {isDanger ? (
          <AlertIcon size="44px" color="danger" />
        ) : (
          <WarningIcon size="48px" color="warning" />
        )}
        <Inscription
          margin={{ vertical: 'spacing12' }}
          size="2xl"
          weight={700}
          textAlign="center"
          data-testid={`${config.testId}_title`}
        >
          {config.title}
        </Inscription>
        <Box
          width="100%"
          pad={{ vertical: 'spacing16', horizontal: 'spacing24' }}
          round="container2Round"
          background={{ color: isDanger ? 'dangerContainer' : 'warningContainer' }}
          border={{ size: '1px', color: isDanger ? 'dangerBorder' : 'warningBorder' }}
          style={{ position: 'relative' }}
        >
          <Box
            height="2px"
            round="small"
            background={isDanger ? 'danger' : 'warning'}
            style={{
              position: 'absolute', top: '-1px', left: '24px', right: '24px',
            }}
          />
          <Inscription weight={700} size="medium">
            What happens next:
          </Inscription>
          <Box as="ul" margin={{ left: 'spacing16' }} style={{ listStyle: 'disc' }}>
            {config.bullets.map((bullet) => (
              <li key={bullet}>
                <Inscription size="medium" color="textBody">
                  {bullet}
                </Inscription>
              </li>
            ))}
          </Box>
        </Box>
        <Box margin={{ top: 'spacing24' }} direction="row" gap="spacing12">
          <AppButton
            buttonType="secondary"
            width="180px"
            testId={`${config.testId}_close`}
            onClick={onClose}
          >
            Close
          </AppButton>
          <AppButton
            width="180px"
            {...(config.confirmColor === FraudReviewModalConfirmColor.Red
              ? { color: FraudReviewModalConfirmColor.Red }
              : {})}
            testId={`${config.testId}_confirm`}
            onClick={onConfirm}
          >
            {isSubmitting ? (
              <Preloader testId={`${config.testId}_preloader`} color={isDanger ? 'error' : undefined} />
            ) : (
              config.confirmLabel
            )}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default FraudReviewModal;
